import {
    Alert,
    AlertDescription,
    AlertIcon,
    Box,
    Button,
    Flex,
    HStack,
    Switch,
    Text,
    VStack,
} from '@chakra-ui/react';
import type { PartType } from '../../../../hooks/useGetSettings';
import {
    SettingsCard,
    SettingsFlowForm,
    SettingsNumberInput,
    SettingsRow,
    PARTBAY_BLUE,
    PARTBAY_BLUE_TINT,
} from '../../../../components/settings/primitives';
import { summarizePriceMatchEntry } from '../../../../components/settings/priceMatchCriteria';
import { getPartTypeLabel } from '../../../../utils/chartUtils';
import { Notice, Select } from '../../../../ui';
import DrpProhibitedVendorsTable from './DrpProhibitedVendorsTable';
import type { DrpDraft } from './drpDraft';

const APU_PART_TYPE_OPTIONS: PartType[] = ['oem', 'oe-surplus', 'a/m'];

/** This carrier's place on the shared APU maximization list. */
export interface DrpApuOverride {
    /** Organization default, used when this carrier has no override. */
    defaultPartType: PartType;
    /** Null means this carrier uses the organization default. */
    partType: PartType | null;
    disabled: boolean;
    /** Same cases the Re-keying screen warns about. */
    notice: 'inactive' | 'ccc' | 'california';
    onChange: (partType: PartType | null) => void;
}

export interface DrpOptionsStepProps {
    draft: DrpDraft;
    onChange: (next: DrpDraft) => void;
    /** Opens the custom price-match ranking editor. */
    onEditCustomRanking: () => void;
    /** Sub companies these settings also cover, named so it is not a surprise. */
    sharedWith?: Array<string>;
    /**
     * APU part type for this carrier. Writes the same
     * `rekeySettings.apuMaximization.custom` entry the Re-keying screen does.
     */
    apu?: DrpApuOverride;
}

/**
 * Second step: how Yaxle treats the carrier. Ordering and price matching are
 * one table of settings; the vendors the carrier prohibits are another. Both sit
 * in the standard settings column, the same width as the Quoting and EMS
 * screens, rather than running the whole viewport.
 */
const DrpOptionsStep = ({
    draft,
    onChange,
    onEditCustomRanking,
    sharedWith = [],
    apu,
}: DrpOptionsStepProps) => {
    const hasRanking = draft.priceMatchCustomRanking.length > 0;

    // Estimate is on whenever CCC, PartsTrader, and custom are all off. It can
    // also stay on beside CCC or PartsTrader. Custom still replaces those two.
    const withEstimate = (next: DrpDraft): DrpDraft => {
        const othersOff = !next.priceMatchCCC && !next.priceMatchPartstrader && !next.priceMatchCustom;
        return othersOff ? { ...next, priceMatchEstimate: true } : next;
    };

    // CCC/PartsTrader and a custom ranking are mutually exclusive: the ranking
    // replaces the marketplace comparison rather than adding to it.
    const handleToggleCCC = (checked: boolean) => {
        onChange(withEstimate({
            ...draft,
            priceMatchCCC: checked,
            priceMatchCustom: checked ? false : draft.priceMatchCustom,
        }));
    };

    const handleTogglePartstrader = (checked: boolean) => {
        onChange(withEstimate({
            ...draft,
            priceMatchPartstrader: checked,
            priceMatchCustom: checked ? false : draft.priceMatchCustom,
        }));
    };

    const handleToggleCustom = (checked: boolean) => {
        if (!checked) {
            onChange(withEstimate({ ...draft, priceMatchCustom: false }));
            return;
        }
        onChange({
            ...draft,
            priceMatchCustom: true,
            priceMatchCCC: false,
            priceMatchPartstrader: false,
        });
        // A ranking with no entries is the same as the option being off, so
        // turning it on opens the editor straight away.
        if (!hasRanking) onEditCustomRanking();
    };

    const noMarketplaceMatch =
        !draft.priceMatchCCC && !draft.priceMatchPartstrader && !draft.priceMatchCustom;

    const handleToggleEstimate = (checked: boolean) => {
        if (!checked && noMarketplaceMatch) return;
        onChange({ ...draft, priceMatchEstimate: checked });
    };

    return (
        <SettingsFlowForm>
            <VStack align="stretch" spacing={6}>
                {/* One entry, several carriers: say so where the rules are set,
                    not only on the step where the group was picked. */}
                {sharedWith.length > 0 && (
                    <Alert status="info" borderRadius="md" alignItems="flex-start">
                        <AlertIcon mt="2px" />
                        <AlertDescription fontSize="sm">
                            These settings also apply to {sharedWith.join(', ')}. Branch a sub
                            company on the Carrier step to give it rules of its own.
                        </AlertDescription>
                    </Alert>
                )}
                <SettingsCard
                    title="Ordering and price matching"
                    description="How Yaxle prices and orders parts for this carrier"
                >
                    <SettingsRow
                        label="PartsTrader ordering threshold"
                        description="Maximum discount percentage required before ordering from this carrier."
                        control={
                            <SettingsNumberInput
                                value={draft.orderingThreshold}
                                onChange={(orderingThreshold) =>
                                    onChange({ ...draft, orderingThreshold })
                                }
                                min={0}
                                max={99}
                                unit="%"
                                width="96px"
                                placeholder="—"
                                justify="flex-end"
                                ariaLabel="PartsTrader ordering threshold"
                            />
                        }
                    />
                    <SettingsRow
                        label="Price match to CCC Source Parts"
                        description="We price match to the most cost-effective option in CCC Source Parts, so the estimate does not have to be written compliant. If PartsTrader is on and cheaper, that price is used instead."
                        control={
                            <Switch
                                size="lg"
                                colorScheme="partbay-blue"
                                isChecked={draft.priceMatchCCC}
                                onChange={(e) => handleToggleCCC(e.target.checked)}
                                aria-label="Price match to CCC Source Parts"
                            />
                        }
                    />
                    <SettingsRow
                        label="Price match to PartsTrader"
                        description="We price match to the most cost-effective option in PartsTrader. If CCC Source Parts is on and cheaper, that price is used instead."
                        control={
                            <Switch
                                size="lg"
                                colorScheme="partbay-blue"
                                isChecked={draft.priceMatchPartstrader}
                                onChange={(e) => handleTogglePartstrader(e.target.checked)}
                                aria-label="Price match to PartsTrader"
                            />
                        }
                    />
                    <SettingsRow
                        label="Price match to the estimate"
                        description="We price match to the list price in the estimate. This stays on when the options above are all off, and it can stay on with CCC Source Parts or PartsTrader."
                        control={
                            <Switch
                                size="lg"
                                colorScheme="partbay-blue"
                                isChecked={noMarketplaceMatch || draft.priceMatchEstimate}
                                isDisabled={noMarketplaceMatch}
                                onChange={(e) => handleToggleEstimate(e.target.checked)}
                                aria-label="Price match to the estimate"
                            />
                        }
                    />
                    <SettingsRow
                        label="Custom price match"
                        description="Rank the sources to price match against instead of using a marketplace."
                        control={
                            <Switch
                                size="lg"
                                colorScheme="partbay-blue"
                                isChecked={draft.priceMatchCustom}
                                onChange={(e) => handleToggleCustom(e.target.checked)}
                                aria-label="Custom price match"
                            />
                        }
                    >
                        {draft.priceMatchCustom && (
                            <Box
                                borderWidth="1px"
                                borderColor="gray.200"
                                borderRadius="md"
                                bg="gray.50"
                                p={3}
                            >
                                <Flex justify="space-between" align="center" mb={hasRanking ? 2 : 0}>
                                    <Text fontSize="xs" fontWeight="semibold" color="gray.600">
                                        Custom ranking
                                    </Text>
                                    <Button size="xs" variant="ghost" onClick={onEditCustomRanking}>
                                        Edit
                                    </Button>
                                </Flex>
                                {hasRanking ? (
                                    <VStack align="stretch" spacing={1}>
                                        {draft.priceMatchCustomRanking.map((entry, index) => (
                                            <HStack key={index} spacing={2} align="flex-start">
                                                <Flex
                                                    align="center"
                                                    justify="center"
                                                    boxSize="18px"
                                                    borderRadius="full"
                                                    bg={PARTBAY_BLUE_TINT}
                                                    color={PARTBAY_BLUE}
                                                    fontSize="0.65rem"
                                                    fontWeight="bold"
                                                    flexShrink={0}
                                                    mt="1px"
                                                >
                                                    {index + 1}
                                                </Flex>
                                                <Text fontSize="xs" color="gray.700">
                                                    {summarizePriceMatchEntry(entry)}
                                                </Text>
                                            </HStack>
                                        ))}
                                    </VStack>
                                ) : (
                                    <Text fontSize="xs" color="gray.500">
                                        No entries configured yet.
                                    </Text>
                                )}
                            </Box>
                        )}
                    </SettingsRow>
                </SettingsCard>

                {draft.sourcePartsRequired && !draft.priceMatchCCC && (
                    <Alert status="warning" borderRadius="md">
                        <AlertIcon />
                        <AlertDescription fontSize="sm">
                            By turning this off, you confirm that we won't price match to the MCE in
                            CCC Source Parts and cause missed opportunities. This may not apply if
                            your state / arrangement is different, but it is on automatically because
                            it applies to all other shops on the platform.
                        </AlertDescription>
                    </Alert>
                )}
                {draft.partstraderRequired && !draft.priceMatchPartstrader && (
                    <Alert status="warning" borderRadius="md">
                        <AlertIcon />
                        <AlertDescription fontSize="sm">
                            By turning this off, you confirm that we won't price match to the MCE in
                            PartsTrader and cause missed opportunities. This may not apply if your
                            state / arrangement is different, but it is on automatically because it
                            applies to all other shops on the platform.
                        </AlertDescription>
                    </Alert>
                )}
                {apu && (
                    <Box opacity={apu.disabled ? 0.5 : 1}>
                        <SettingsCard
                            title="APU maximization"
                            description="What part type price-matched OEM parts are re-keyed as for this carrier. Choosing a type stores an override on the same list as Re-keying; the organization default applies otherwise."
                        >
                            <Box py={4}>
                                <Notice tone="warning">
                                    {apu.notice === 'inactive' ? (
                                        <>
                                            <strong>Heads up — currently inactive.</strong>{' '}
                                            Because <strong>Create Manual POs</strong> is enabled, all OEM parts will be re-keyed as OEM and these settings will be ignored.
                                            {' '}It is also ignored when the estimate&apos;s repair facility is located in California.
                                        </>
                                    ) : apu.notice === 'ccc' ? (
                                        <>
                                            If the estimate&apos;s repair facility is located in <strong>California</strong>, this setting is ignored and all OEM parts will be re-keyed as OEM. If <strong>Create Manual POs</strong> is enabled, then PO creation will fail for all POs with parts re-keyed to the APU type.
                                        </>
                                    ) : (
                                        <>
                                            If the estimate&apos;s repair facility is located in <strong>California</strong>, this setting is ignored and all OEM parts will be re-keyed as OEM.
                                        </>
                                    )}
                                </Notice>
                            </Box>
                            <SettingsRow
                                label="Part type"
                                description={`Organization default is ${getPartTypeLabel(apu.defaultPartType)}. An override here covers this carrier${sharedWith.length > 0 ? ` and ${sharedWith.join(', ')}` : ''}.`}
                                control={
                                    <Select
                                        size="sm"
                                        w="180px"
                                        aria-label="APU part type for this carrier"
                                        value={apu.partType ?? ''}
                                        isDisabled={apu.disabled}
                                        bg="white"
                                        onChange={(event) => {
                                            const value = event.target.value;
                                            apu.onChange(value ? (value as PartType) : null);
                                        }}
                                    >
                                        <option value="">
                                            Default ({getPartTypeLabel(apu.defaultPartType)})
                                        </option>
                                        {APU_PART_TYPE_OPTIONS.map((partType) => (
                                            <option key={partType} value={partType}>
                                                {getPartTypeLabel(partType)}
                                            </option>
                                        ))}
                                    </Select>
                                }
                            />
                        </SettingsCard>
                    </Box>
                )}

                <SettingsCard
                    title="Prohibited vendors"
                    description="Vendors already prohibited in Vendor settings do not need to be added here"
                >
                    <SettingsRow
                        label="Vendors"
                        description="Parts from these vendors are never selected for this carrier."
                        isStacked
                        control={
                            <DrpProhibitedVendorsTable
                                value={draft.prohibitedSuppliers}
                                onChange={(prohibitedSuppliers) =>
                                    onChange({ ...draft, prohibitedSuppliers })
                                }
                            />
                        }
                    />
                </SettingsCard>
            </VStack>
        </SettingsFlowForm>
    );
};

export default DrpOptionsStep;
